import {
  Component,
  OnDestroy,
  OnInit,
  inject,
  signal,
  PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription, filter, timer } from 'rxjs';
import { ServiceUnavailableStateService } from 'src/app/shared/services/service-unavailable-state.service';

@Component({
  selector: 'app-service-unavailable',
  templateUrl: './service-unavailable.component.html',
  styleUrls: ['./service-unavailable.component.scss'],
})
export class ServiceUnavailableComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly stateService = inject(ServiceUnavailableStateService);
  private readonly platformId = inject<any>(PLATFORM_ID);

  private readonly RETRY_DELAY_MS = 10000;

  // Signal: con detección de cambios zoneless solo signals/CDR actualizan la plantilla.
  showFailedUrl = signal(false);
  failedUrl: string | null = null;

  private retrySubscription: Subscription | null = null;
  private navigationSubscription: Subscription | null = null;

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return; // SSR: solo mensaje estático
    }
    this.failedUrl = this.stateService.failedUrl;

    // En los bounces (reintento fallido -> vuelta a /503) el componente NO se
    // recrea (el router-outlet nunca se desactiva), por lo que el temporizador
    // se re-arma escuchando NavigationEnd en vez de confiar solo en ngOnInit.
    this.navigationSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        const url = (event as NavigationEnd).urlAfterRedirects;
        if (url.endsWith('/503')) {
          this.armRetry();
        } else {
          this.disarmRetry();
        }
      });

    this.armRetry(); // idempotente: cubre el caso de que el evento ya se haya emitido
  }

  ngOnDestroy(): void {
    this.disarmRetry();
    this.navigationSubscription?.unsubscribe();
  }

  /** Idempotente: desarma cualquier temporizador previo antes de armar el nuevo. */
  private armRetry(): void {
    this.disarmRetry();
    const failedPath = this.stateService.failedPath;
    if (!failedPath) {
      return; // navegación directa a /503: solo mensaje estático
    }
    if (!this.stateService.canRetry()) {
      // Tercer intento fallido: mostrar la URL guardada y no reintentar más.
      this.failedUrl = this.stateService.failedUrl;
      this.showFailedUrl.set(true);
      return;
    }
    this.retrySubscription = timer(this.RETRY_DELAY_MS).subscribe(() => {
      // El usuario pudo navegar manualmente lejos de /503 durante la espera.
      if (!window.location.pathname.endsWith('/503')) {
        return;
      }
      this.stateService.incrementAttempts();
      void this.router
        .navigateByUrl(failedPath, { replaceUrl: true })
        .then((success) => {
          if (success) {
            // El backend se recuperó y la navegación completó: reset diferido.
            this.stateService.scheduleSuccessReset();
          }
          // Si falla/cancela, el bounce a /503 emite otro NavigationEnd y se re-arma.
        });
    });
  }

  private disarmRetry(): void {
    this.retrySubscription?.unsubscribe();
    this.retrySubscription = null;
  }
}
